import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { catchError, switchMap, tap, takeUntil } from 'rxjs/operators';
import { forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { DateTime } from 'luxon';
import { ServerTokenService } from '../../../services/server-token.service';
import { EligibleRecipient, ReportRecipient, ScheduleFormData } from '../../report-notifications.types';
import { formatFrequencyLabel, formatTimeOfDay } from './schedule-label';
import { isReportsAdmin } from 'list-types-public';

const SCHEDULE_ZONE = 'America/New_York';
// Report types whose email generator can render the table in the body.
const TABLE_IN_BODY_REPORT_TYPES = ['event_report'];
const LOAD_ERROR = 'Failed to load schedule data. Saving is disabled to prevent overwriting existing settings.';

@Component({
  selector: 'app-scheduled-notifications',
  templateUrl: './scheduled-notifications.component.html',
  styleUrls: ['./scheduled-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduledNotificationsComponent implements OnChanges, OnDestroy {
  @Input() reportId!: string;
  @Input() reportTypeWire!: string;
  /**
   * Collapse the recipient picker behind a one-line summary, opening only when
   * the admin goes to change it. Opt-in: a full roster of every eligible user
   * is a lot of page for something that is usually set once, but the screens
   * that already embed this block expect the open table, so they keep it.
   */
  @Input() collapsibleRecipients = false;
  /** Swaps the next-send emoji for a tintable icon. OFF by default: every
   *  host that does not ask keeps the emoji it has always shown. */
  @Input() themedIcon = false;
  /** Section cards and report-shell controls, matching the Event Reports
   *  edit form. OFF by default, so every other host keeps its look. */
  @Input() cardLayout = false;
  /**
   * Whether a pristine block should still be PERSISTED on save.
   *
   * OFF by default, because most hosts embed this block beside unrelated
   * settings and call save() unconditionally: there, a Save that the user
   * meant for the other settings must not bring a schedule into existence
   * that nobody asked for.
   *
   * ON for a host whose Save means "set this email up" - there the rendered
   * defaults ARE the user's answer, and skipping the write reported success
   * while persisting nothing, with a confident "Next send:" beside it.
   */
  @Input() createOnSave = false;

  /** Only meaningful when collapsibleRecipients is on. */
  recipientsOpen = false;

  scheduleForm: ScheduleFormData = {
    frequency: 'weekly',
    weeklyDays: [1],
    monthlyDays: [1],
    timeEST: '09:00',
    enabled: true,
    includeTableInBody: false,
  };

  eligibleRecipients: EligibleRecipient[] = [];
  filteredEligibleRecipients: EligibleRecipient[] = [];
  selectedRecipientIds: Set<string> = new Set();
  recipientSearchFilter = '';
  scheduleLoading = false;
  scheduleLoadError: string | null = null;

  private scheduleDirty = false;
  /** True once the server confirms a schedule ROW exists (loaded or saved).
   *  While false, save() must not skip the PUT even when pristine - the
   *  rendered defaults are not persisted state yet. */
  private scheduleExists = false;
  /** True once the load ACTUALLY finished. `scheduleExists === false` alone is
   *  ambiguous: it is also the value while the GET is in flight and after a GET
   *  that failed, and createOnSave must never act on that ambiguity. */
  private scheduleLoaded = false;
  private destroy$ = new Subject<void>();

  constructor(
    private server: ServerTokenService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportId'] && this.reportId) {
      this.loadScheduleData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFrequencyChange(freq: 'daily' | 'weekly' | 'monthly'): void {
    this.scheduleForm = { ...this.scheduleForm, frequency: freq };
    this.scheduleDirty = true;
    this.cdr.markForCheck();
  }

  toggleWeeklyDay(day: number): void {
    const current = this.scheduleForm.weeklyDays;
    if (current.includes(day) && current.length <= 1) return;
    const nextDays = current.includes(day)
      ? current.filter(d => d !== day)
      : [...current, day];
    this.scheduleForm = { ...this.scheduleForm, weeklyDays: nextDays };
    this.scheduleDirty = true;
    this.cdr.markForCheck();
  }

  onMonthlyDayChange(day: number | null): void {
    const safeDay = Math.max(1, Math.min(31, Number(day) || 1));
    this.scheduleForm = { ...this.scheduleForm, monthlyDays: [safeDay] };
    this.scheduleDirty = true;
    this.cdr.markForCheck();
  }

  onTimeEstChange(time: string): void {
    this.scheduleForm = { ...this.scheduleForm, timeEST: time };
    this.scheduleDirty = true;
    this.cdr.markForCheck();
  }

  get supportsTableInBody(): boolean {
    return TABLE_IN_BODY_REPORT_TYPES.includes(this.reportTypeWire);
  }

  onIncludeTableInBodyChange(checked: boolean): void {
    this.scheduleForm = { ...this.scheduleForm, includeTableInBody: checked };
    this.scheduleDirty = true;
    this.cdr.markForCheck();
  }

  onRecipientSearchChange(): void {
    const filter = (this.recipientSearchFilter || '').toLowerCase();
    this.filteredEligibleRecipients = this.eligibleRecipients.filter(u =>
      u.privateName.toLowerCase().includes(filter) || u.email.toLowerCase().includes(filter),
    );
    this.cdr.markForCheck();
  }

  toggleRecipient(userId: string): void {
    const next = new Set(this.selectedRecipientIds);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    this.selectedRecipientIds = next;
    this.scheduleDirty = true;
    this.cdr.markForCheck();
  }

  toggleAllRecipients(): void {
    const allSelected = this.filteredEligibleRecipients.every(u => this.selectedRecipientIds.has(u._id));
    const next = new Set(this.selectedRecipientIds);
    for (const u of this.filteredEligibleRecipients) {
      if (allSelected) next.delete(u._id); else next.add(u._id);
    }
    this.selectedRecipientIds = next;
    this.scheduleDirty = true;
    this.cdr.markForCheck();
  }

  toggleRecipientsOpen(): void {
    this.recipientsOpen = !this.recipientsOpen;
    this.cdr.markForCheck();
  }

  /** "Ruchie Bergman, Sara Levy and 3 others" - who actually receives this. */
  get recipientSummary(): string {
    const names = this.eligibleRecipients
      .filter(u => this.selectedRecipientIds.has(u._id))
      .map(u => u.privateName || u.email);
    if (!names.length) return 'Nobody yet';
    if (names.length <= 2) return names.join(' and ');
    const others = names.length - 2;
    return `${names[0]}, ${names[1]} and ${others} other${others === 1 ? '' : 's'}`;
  }

  get allRecipientsSelected(): boolean {
    return this.filteredEligibleRecipients.length > 0 &&
      this.filteredEligibleRecipients.every(u => this.selectedRecipientIds.has(u._id));
  }

  get someRecipientsSelected(): boolean {
    return this.filteredEligibleRecipients.some(u => this.selectedRecipientIds.has(u._id)) && !this.allRecipientsSelected;
  }

  save(): Observable<unknown> {
    // Until the in-flight load lands, the form and recipient set still hold the
    // previously selected report's values, and the server syncs membership to
    // exactly what we submit.
    if (this.scheduleLoading) return throwError(new Error('Schedule is still loading. Try again in a moment.'));
    if (this.scheduleLoadError) return throwError(new Error('Schedule data failed to load. Refresh the page and try again.'));
    // Every control in this block sets scheduleDirty, so "pristine" means the
    // user did not touch the schedule at all. Then: write nothing - unless the
    // host asked for createOnSave AND no row exists yet, which is the one case
    // where the rendered defaults are the answer rather than a placeholder.
    const pristine = !this.scheduleDirty;
    // Creating from pristine defaults is only honest once the load came back and
    // said there is no row. While loading, the rendered defaults are a
    // placeholder, and writing them would overwrite a live schedule and PUT
    // recipientUserIds: [], which the server reads as "remove everyone".
    const mayCreate = this.createOnSave && this.scheduleLoaded && !this.scheduleExists;
    if (pristine && !mayCreate) return of(null);
    if (pristine && mayCreate) {
      // scheduleExists=false is also what a swallowed load failure looks like
      // (the load's catchError maps any error to null), so before creating,
      // re-ask the server WITHOUT that safety net: an existing row cancels the
      // create and repopulates the form, and a failed check fails the save
      // loudly instead of overwriting a live schedule with factory defaults.
      return this.server.getWithToken<ScheduleFormData | null>(
        `report-notifications/reports/${this.reportId}/schedule?reportType=${this.reportTypeWire}`,
      ).pipe(
        switchMap(schedule => {
          if (schedule) {
            this.scheduleExists = true;
            this.applySchedule(schedule);
            this.cdr.markForCheck();
            return of(null);
          }
          return this.putNotificationSettings();
        }),
      );
    }
    return this.putNotificationSettings();
  }

  private putNotificationSettings(): Observable<unknown> {
    return this.server.putWithToken(
      `report-notifications/reports/${this.reportId}/notification-settings`,
      {
        reportType: this.reportTypeWire,
        schedule: this.scheduleForm,
        recipientUserIds: Array.from(this.selectedRecipientIds),
      },
    ).pipe(
      tap(() => {
        this.scheduleDirty = false;
        this.scheduleExists = true;
        this.cdr.markForCheck();
      }),
    );
  }

  private applySchedule(schedule: ScheduleFormData): void {
    this.scheduleForm = {
      frequency: schedule.frequency,
      weeklyDays: schedule.weeklyDays || [1],
      monthlyDays: schedule.monthlyDays || [1],
      timeEST: schedule.timeEST,
      enabled: schedule.enabled,
      includeTableInBody: schedule.includeTableInBody === true,
    };
  }

 getRoleBadge(roleNumber: number): string {
    return isReportsAdmin(roleNumber) ? 'Admin' : 'Viewer';
  }

  getNextSendPreview(): string {
    const [hours, minutes] = this.scheduleForm.timeEST.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return '';
    const nowEt = DateTime.now().setZone(SCHEDULE_ZONE);
    const fmt = (dt: DateTime) =>
      `${dt.toFormat('ccc, LLL d')} at ${this.formatTime(dt.hour, dt.minute)} EST`;

    if (this.scheduleForm.frequency === 'daily') {
      let next = nowEt.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
      if (next <= nowEt) next = next.plus({ days: 1 });
      return fmt(next);
    }

    if (this.scheduleForm.frequency === 'weekly') {
      const sortedDays = [...this.scheduleForm.weeklyDays].sort();
      const todayDow = nowEt.weekday % 7;
      for (let offset = 0; offset <= 7; offset++) {
        const checkDay = (todayDow + offset) % 7;
        if (sortedDays.includes(checkDay)) {
          const candidate = nowEt
            .plus({ days: offset })
            .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
          if (candidate > nowEt) return fmt(candidate);
        }
      }
      return fmt(nowEt.plus({ days: 7 }).set({ hour: hours, minute: minutes, second: 0, millisecond: 0 }));
    }

    const sortedMonthDays = [...(this.scheduleForm.monthlyDays || [1])].sort((a, b) => a - b);
    for (const day of sortedMonthDays) {
      const clamped = Math.min(day, nowEt.daysInMonth ?? 31);
      const candidate = nowEt.set({ day: clamped, hour: hours, minute: minutes, second: 0, millisecond: 0 });
      if (candidate > nowEt) return fmt(candidate);
    }
    const nextMonth = nowEt.plus({ months: 1 }).startOf('month');
    const nextDay = Math.min(sortedMonthDays[0], nextMonth.daysInMonth ?? 31);
    return fmt(nextMonth.set({ day: nextDay, hour: hours, minute: minutes, second: 0, millisecond: 0 }));
  }

  getFrequencyLabel(): string {
    // Shared with the Scheduled Emails list so both describe a schedule identically.
    return formatFrequencyLabel(this.scheduleForm);
  }

  trackByRecipientId = (_: number, user: EligibleRecipient) => user._id;

  private formatTime(h: number, m: number): string {
    return formatTimeOfDay(h, m);
  }

  private loadScheduleData(): void {
    const requestedReportId = this.reportId;
    this.scheduleLoading = true;
    this.scheduleLoadError = null;
    // A (re)load must not leave the previous report's verdict behind - the
    // save() guards read all three of these.
    this.scheduleLoaded = false;
    this.scheduleExists = false;
    this.scheduleDirty = false;
    // A caught read yields the same empty shape as "nothing configured yet", so
    // record the failure - saving that shape would delete the real settings.
    let loadFailed = false;
    forkJoin({
      schedule: this.server.getWithToken<ScheduleFormData | null>(
        `report-notifications/reports/${this.reportId}/schedule?reportType=${this.reportTypeWire}`,
      ).pipe(catchError(() => { loadFailed = true; return of<ScheduleFormData | null>(null); })),
      eligible: this.server.getWithToken<EligibleRecipient[]>(
        `report-notifications/reports/${this.reportId}/eligible-recipients?reportType=${this.reportTypeWire}`,
      ).pipe(catchError(() => { loadFailed = true; return of<EligibleRecipient[]>([]); })),
      recipients: this.server.getWithToken<ReportRecipient[]>(
        `report-notifications/reports/${this.reportId}/recipients?reportType=${this.reportTypeWire}`,
      ).pipe(catchError(() => { loadFailed = true; return of<ReportRecipient[]>([]); })),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ schedule, eligible, recipients }) => {
          // A newer report was selected while this load was in flight.
          if (this.reportId !== requestedReportId) return;
          if (loadFailed) {
            this.scheduleLoadError = LOAD_ERROR;
            this.scheduleLoading = false;
            this.cdr.markForCheck();
            return;
          }
          this.scheduleExists = !!schedule;
          this.scheduleLoaded = true;
          if (schedule) this.applySchedule(schedule);
          this.eligibleRecipients = eligible || [];
          this.filteredEligibleRecipients = [...this.eligibleRecipients];
          this.selectedRecipientIds = new Set((recipients || []).map(r => r.userId));
          this.scheduleDirty = false;
          this.scheduleLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.scheduleLoadError = LOAD_ERROR;
          this.scheduleLoading = false;
          this.cdr.markForCheck();
        },
      });
  }
}
